import { INIT_ERRORS } from '@send-frontend/apps/send/const';
import { FolderStore } from '@send-frontend/apps/send/stores/folder-store.types';
import { Keychain } from '@send-frontend/lib/keychain';
import { useFolderStore } from '@send-frontend/stores';
import { UserStoreType as UserStore } from '@send-frontend/stores/user-store';

/**
 * Loads user and keychain from storage; creates default folder if necessary.
 * @param {UserStore} userStore - Pinia store for managing user.
 * @param {Keychain} keychain - Instance of Keychain class.
 * @param {FolderStore} folderStore - Pinia store for managing folders.
 * @return {Promise<INIT_ERRORS>} - Returns Promise of 0 (success) or an error code typed by INIT_ERRORS.
 */
async function init(
  userStore: UserStore,
  keychain: Keychain,
  folderStore: FolderStore | ReturnType<typeof useFolderStore>
): Promise<INIT_ERRORS> {
  const hasUser = await userStore.loadFromLocalStorage();
  const hasKeychain = await keychain.load();

  if (!hasUser) {
    return INIT_ERRORS.NO_USER;
  }

  if (!hasKeychain) {
    return INIT_ERRORS.NO_KEYCHAIN;
  }

  await folderStore.sync();
  const defaultFolder = folderStore?.defaultFolder;
  const defaultFolderKeyIsMissing =
    defaultFolder && !keychain.keys[defaultFolder.id];

  if (defaultFolderKeyIsMissing) {
    console.warn(
      `Default folder ${defaultFolder.id} exists but has no key. Deleting orphaned container and recreating.`
    );
    await folderStore.deleteFolder(defaultFolder.id);
  }

  if (!defaultFolder || defaultFolderKeyIsMissing) {
    const createFolderResp = await folderStore.createFolder();
    if (!createFolderResp?.id) {
      return INIT_ERRORS.COULD_NOT_CREATE_DEFAULT_FOLDER;
    }
  }

  return INIT_ERRORS.NONE;
}

export default init;
